import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Clock, Calendar, XCircle, Loader2, RefreshCw, List, CalendarDays } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  useSchedules,
  useToggleScheduleEnabled,
  useTriggerSchedule,
  useDeleteSchedule,
  useRunningSchedules
} from '@/hooks/useScheduling';
import { useExecutionHistory } from '@/hooks/useDataCollection';
import { Schedule, ScheduleWithRunningStatus } from '@/services/api/scheduling';
import { useScheduleViewPreference, ScheduleViewMode } from '@/hooks/useScheduleViewPreference';
import { normalizeStatus, isRunningStatus } from '@/utils/executionStatus';
import { getScheduledTimeFromCron } from '@/utils/cronCalendar';

import { CompactScheduleCard } from './CompactScheduleCard';
import { SchedulesCalendarView } from './SchedulesCalendarView';

interface SchedulesListProps {
  tenantId?: string;
  onEdit: (schedule: Schedule) => void;
  onViewHistory: (schedule: Schedule) => void;
  onCreateNew: () => void;
}

export const SchedulesList: React.FC<SchedulesListProps> = ({
  tenantId,
  onEdit,
  onViewHistory,
  onCreateNew
}) => {
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<Schedule | null>(null);
  const [viewMode, setViewMode] = useScheduleViewPreference();

  // Fetch schedules
  const {
    data: schedulesData,
    isLoading,
    error,
    refetch
  } = useSchedules({
    tenant_id: tenantId,
    limit: 100
  });

  // Fetch running status with polling for real-time updates
  const { data: runningData } = useRunningSchedules(tenantId);

  // Fetch execution history as fallback for last run info
  const { data: executionHistory } = useExecutionHistory(100);

  // Mutations
  const toggleEnabledMutation = useToggleScheduleEnabled();
  const triggerMutation = useTriggerSchedule();
  const deleteMutation = useDeleteSchedule();

  // Find last run for a schedule from execution history (fallback)
  const getLastRunFromHistory = (schedule: Schedule) => {
    if (!executionHistory) return null;

    const matchingExecutions = executionHistory
      .filter(exec => exec.task_name === schedule.task_name)
      .sort((a, b) => {
        const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
        const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
        return bTime - aTime;
      });

    const scheduledRun = matchingExecutions.find(exec => exec.trigger_mode === 'scheduled');
    if (scheduledRun) return scheduledRun;

    return matchingExecutions[0] || null;
  };

  // Merge schedules with running status
  const schedules = useMemo(() => {
    const baseSchedules = schedulesData?.schedules || [];
    const runningSchedules = runningData?.schedules || [];

    const runningStatusMap = new Map(
      runningSchedules.map(s => [s.id, { is_running: s.is_running, current_run: s.current_run }])
    );

    return baseSchedules.map(schedule => {
      const runningInfo = runningStatusMap.get(schedule.id);
      const isRunning =
        runningInfo?.is_running || isRunningStatus(normalizeStatus(schedule.last_run_status || ''));

      let lastTriggeredAt = schedule.last_triggered_at;
      let lastRunStatus = schedule.last_run_status;
      const runCount = schedule.run_count;
      let fromHistory = false;

      if (!lastTriggeredAt) {
        const lastRun = getLastRunFromHistory(schedule);
        if (lastRun) {
          lastTriggeredAt = lastRun.started_at;
          lastRunStatus = lastRun.status;
          fromHistory = true;
        }
      }

      return {
        ...schedule,
        is_running: isRunning,
        current_run: runningInfo?.current_run || null,
        last_triggered_at: lastTriggeredAt,
        last_run_status: lastRunStatus,
        run_count: runCount,
        _fromHistory: fromHistory
      } as ScheduleWithRunningStatus & { _fromHistory?: boolean };
    });
  }, [schedulesData, runningData, executionHistory]);

  // Sort schedules by scheduled time for list view
  const sortedSchedules = useMemo(() => {
    return [...schedules].sort((a, b) => {
      const timeA = getScheduledTimeFromCron(a.cron_expression);
      const timeB = getScheduledTimeFromCron(b.cron_expression);
      // Sort by hour first, then by minute
      if (timeA.hour !== timeB.hour) return timeA.hour - timeB.hour;
      return timeA.minute - timeB.minute;
    });
  }, [schedules]);

  // Count running schedules
  const runningCount = runningData?.running_count || schedules.filter(s => s.is_running).length;

  // Handle toggle enabled
  const handleToggleEnabled = (schedule: Schedule) => {
    const newEnabled = !schedule.is_enabled;

    toast({
      title: newEnabled ? 'Schedule Enabled' : 'Schedule Disabled',
      description: `${schedule.schedule_name} has been ${newEnabled ? 'enabled' : 'disabled'}`
    });

    toggleEnabledMutation.mutate(
      { id: schedule.id, is_enabled: newEnabled },
      {
        onError: error => {
          toast({
            title: 'Failed to update schedule',
            description: error instanceof Error ? error.message : 'Unknown error',
            variant: 'destructive'
          });
        }
      }
    );
  };

  // Handle manual trigger
  const handleTrigger = (schedule: Schedule) => {
    toast({
      title: 'Schedule Triggered',
      description: `Manually triggering ${schedule.schedule_name}`
    });

    triggerMutation.mutate(schedule.id, {
      onSuccess: result => {
        toast({
          title: 'Schedule Running',
          description: `Run ID: ${result.run_id}`
        });
      },
      onError: error => {
        toast({
          title: 'Failed to trigger schedule',
          description: error instanceof Error ? error.message : 'Unknown error',
          variant: 'destructive'
        });
      }
    });
  };

  // Handle delete confirmation
  const handleDeleteClick = (schedule: Schedule) => {
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  };

  // Handle delete
  const handleDelete = () => {
    if (!scheduleToDelete) return;

    setDeleteDialogOpen(false);

    toast({
      title: 'Schedule Deleted',
      description: `${scheduleToDelete.schedule_name} has been deleted`
    });

    deleteMutation.mutate(scheduleToDelete.id, {
      onError: error => {
        toast({
          title: 'Failed to delete schedule',
          description: error instanceof Error ? error.message : 'Unknown error',
          variant: 'destructive'
        });
      }
    });

    setScheduleToDelete(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <CardContent className='flex flex-col items-center justify-center py-8 gap-4'>
          <XCircle className='h-8 w-8 text-destructive' />
          <p className='text-muted-foreground'>
            Failed to load schedules: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button variant='outline' onClick={() => refetch()}>
            <RefreshCw className='h-4 w-4 mr-2' />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (schedules.length === 0) {
    return (
      <Card>
        <CardContent className='flex flex-col items-center justify-center py-12 gap-4'>
          <Calendar className='h-12 w-12 text-muted-foreground' />
          <div className='text-center'>
            <h3 className='text-lg font-semibold'>No Schedules Yet</h3>
            <p className='text-muted-foreground mt-1'>
              Create your first schedule to automate data collection.
            </p>
          </div>
          <Button onClick={onCreateNew}>
            <Clock className='h-4 w-4 mr-2' />
            Create Schedule
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className='space-y-4'>
        {/* Header with View Toggle */}
        <div className='flex items-center justify-between flex-wrap gap-3'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-medium'>Schedules</h3>
            <Badge variant='secondary'>{schedules.length}</Badge>
            {runningCount > 0 && (
              <Badge
                variant='outline'
                className='bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400'
              >
                <Loader2 className='h-3 w-3 mr-1 animate-spin' />
                {runningCount} running
              </Badge>
            )}
          </div>

          <div className='flex items-center gap-3'>
            {/* View Toggle */}
            <Tabs
              value={viewMode}
              onValueChange={v => setViewMode(v as ScheduleViewMode)}
              className='h-9'
            >
              <TabsList className='h-9'>
                <TabsTrigger value='list' className='h-7 px-3 gap-1.5'>
                  <List className='h-4 w-4' />
                  <span className='hidden sm:inline'>List</span>
                </TabsTrigger>
                <TabsTrigger value='calendar' className='h-7 px-3 gap-1.5'>
                  <CalendarDays className='h-4 w-4' />
                  <span className='hidden sm:inline'>Calendar</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Button onClick={onCreateNew} size='sm'>
              <Clock className='h-4 w-4 mr-2' />
              Create Schedule
            </Button>
          </div>
        </div>

        {/* View Content */}
        {viewMode === 'list' ? (
          /* List View - Compact Cards (sorted by scheduled time) */
          <div className='space-y-3'>
            {sortedSchedules.map(schedule => (
              <CompactScheduleCard
                key={schedule.id}
                schedule={schedule}
                onToggleEnabled={handleToggleEnabled}
                onTrigger={handleTrigger}
                onEdit={onEdit}
                onDelete={handleDeleteClick}
                onViewHistory={onViewHistory}
                isToggling={toggleEnabledMutation.isPending}
                isTriggering={triggerMutation.isPending}
              />
            ))}
          </div>
        ) : (
          /* Calendar View */
          <SchedulesCalendarView
            schedules={schedules}
            onTrigger={handleTrigger}
            onScheduleClick={onEdit}
            isTriggering={triggerMutation.isPending}
          />
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Schedule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{scheduleToDelete?.schedule_name}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SchedulesList;
